/**
 * CarrierBreakdownStack — gestapelter horizontaler Balken der Carrier-Verteilung
 * (Story 0.3, AC-2)
 *
 * Referenz: claude-design-import/dashboard.html (Carrier-Breakdown, Legende)
 * und hotspot-detail.html (Carrier-Split). Farbtokens --carrier-1..4 aus
 * Story 0.1. Bewusst KEINE Carrier-Logos — neutrale Farbcodes (Design-Vorgabe).
 *
 * Es gibt genau vier Carrier-Farben (--carrier-1..4) mit je eigenem Muster.
 * Liefert der Aufrufer mehr Segmente, wird alles ab dem fuenften zu EINEM
 * neutralen „Andere“-Segment aggregiert (Welle-2-Review F5) — vorher liefen
 * Farbe und Muster zyklisch um und zwei Carrier sahen identisch aus.
 *
 * a11y-Auflage (Form-/Muster-Redundanz): Jedes Segment traegt zusaetzlich zur
 * Farbe ein eigenes Muster (.ui-pattern-1..4 in ui.css). Segmente ab
 * `minLabelPercent` (Default 8 %) bekommen ausserdem eine Inline-Beschriftung
 * im Balken; kleinere Segmente sind ueber die Legende benannt. Der Balken ist
 * role="img" mit vollstaendigem aria-label, die Legende eine echte Liste.
 */
import { useTranslation } from 'react-i18next'
import { formatNumber, formatPercent } from './format'
import { aggregateCarrierSegments, carrierSlot, CARRIER_OTHER_ID } from './scale'
import './ui.css'

export type CarrierSegment = {
  /** Stabiler Key (z. B. Carrier-ID). */
  id: string
  /** Anzeigename, bereits uebersetzt/aufbereitet vom Aufrufer. */
  label: string
  /** Absoluter Wert (z. B. GB). Negative Werte werden auf 0 geklemmt. */
  value: number
  /**
   * Optionale CSS-Farbvariable. Ohne Angabe wird nach Index
   * --carrier-1..4 verwendet — passend zur Muster-Zuordnung.
   */
  colorVar?: string
}

export type CarrierBreakdownStackProps = {
  segments: CarrierSegment[]
  /** Formatiert den absoluten Wert fuer Legende/Tooltip (z. B. „12,4 GB“). */
  formatValue?: (value: number) => string
  /** Ab diesem Prozentanteil wird im Segment beschriftet (Default 8). */
  minLabelPercent?: number
  showLegend?: boolean
  showTotal?: boolean
  className?: string
  'data-testid'?: string
}

export function CarrierBreakdownStack({
  segments,
  formatValue,
  minLabelPercent = 8,
  showLegend = true,
  showTotal = false,
  className = '',
  'data-testid': testId = 'carrier-breakdown-stack',
}: CarrierBreakdownStackProps) {
  const { t } = useTranslation('ui')

  const normalized = segments.map((s) => ({
    ...s,
    value: Number.isFinite(s.value) && s.value > 0 ? s.value : 0,
  }))
  const total = normalized.reduce((sum, s) => sum + s.value, 0)
  // Default-Formatierung: eine Nachkommastelle in der aktiven Sprache. Die
  // Einheit (GB, $, …) kennt nur der Aufrufer und kommt via formatValue.
  const fmtValue = formatValue ?? ((v: number) => formatNumber(v, 1))

  if (normalized.length === 0 || total <= 0) {
    return (
      <div className={`ui-carrier ${className}`.trim()} data-testid={testId}>
        <p className="ui-carrier__empty" data-testid={`${testId}-empty`}>
          {t('carrierBreakdown.empty')}
        </p>
      </div>
    )
  }

  // Welle-2-Review F5: Es gibt nur vier Carrier-Farben mit je eigenem Muster.
  // Frueher wiederholte sich beides ab dem 5. Segment zyklisch — zwei Carrier
  // sahen dann identisch aus. Jetzt werden alle weiteren zu EINEM neutralen
  // „Andere“-Segment zusammengefasst.
  const { visible, otherValue, otherCount } = aggregateCarrierSegments(normalized)

  type Row = {
    id: string
    label: string
    value: number
    fraction: number
    slot: number
    isOther: boolean
    percentText: string
    valueText: string
    color: string
    patternClass: string
  }

  const rows: Row[] = visible.map((segment, index) => {
    const fraction = segment.value / total
    const slot = carrierSlot(index)
    return {
      id: segment.id,
      label: segment.label,
      value: segment.value,
      fraction,
      slot,
      isOther: false,
      percentText: formatPercent(fraction),
      valueText: fmtValue(segment.value),
      color: segment.colorVar ?? `var(--carrier-${slot})`,
      patternClass: `ui-pattern-${slot}`,
    }
  })

  if (otherCount > 0) {
    const fraction = otherValue / total
    rows.push({
      id: CARRIER_OTHER_ID,
      label: t('carrierBreakdown.otherLabel', { carriers: otherCount }),
      value: otherValue,
      fraction,
      slot: 0,
      isOther: true,
      percentText: formatPercent(fraction),
      valueText: fmtValue(otherValue),
      color: 'var(--carrier-other, var(--text-subtle))',
      patternClass: 'ui-pattern-other',
    })
  }

  const summary = rows
    .map((r) =>
      t('carrierBreakdown.segmentSummary', {
        label: r.label,
        percent: r.percentText,
        value: r.valueText,
      })
    )
    .join(', ')

  return (
    <div className={`ui-carrier ${className}`.trim()} data-testid={testId}>
      <div
        className="ui-carrier__bar"
        role="img"
        aria-label={t('carrierBreakdown.ariaLabel', { summary })}
        data-testid={`${testId}-bar`}
      >
        {rows.map((r) => {
          const percent = r.fraction * 100
          return (
            <div
              key={r.id}
              className={`ui-carrier__seg ${r.patternClass}`}
              data-testid={`${testId}-segment-${r.id}`}
              data-slot={r.slot}
              data-other={r.isOther ? 'true' : undefined}
              data-percent={percent.toFixed(2)}
              style={{ width: `${percent}%`, backgroundColor: r.color }}
            >
              {percent >= minLabelPercent && (
                <span className="ui-carrier__seg-label">{r.percentText}</span>
              )}
            </div>
          )
        })}
      </div>

      {showLegend && (
        <ul
          className="ui-carrier__legend"
          aria-label={t('carrierBreakdown.legendLabel')}
          data-testid={`${testId}-legend`}
        >
          {rows.map((r) => (
            <li key={r.id} className="ui-carrier__legend-item">
              <span
                className={`ui-carrier__swatch ${r.patternClass}`}
                aria-hidden="true"
                style={{ backgroundColor: r.color }}
              />
              <span>
                {t('carrierBreakdown.segmentSummary', {
                  label: r.label,
                  percent: r.percentText,
                  value: r.valueText,
                })}
              </span>
            </li>
          ))}
        </ul>
      )}

      {showTotal && (
        <p className="ui-carrier__total" data-testid={`${testId}-total`}>
          {t('carrierBreakdown.totalLabel', { value: fmtValue(total) })}
        </p>
      )}
    </div>
  )
}
